import { useState } from 'preact/hooks';
import type { GameStats, GameView } from '../engine';
import { useSeatColor } from './colors';

const W = 360;
const H = 190;
const PAD = { left: 28, right: 12, top: 12, bottom: 30 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;
const INK = '#1b1b1b';
const MUTED = '#4f5b6e';
const GRID = '#dfe5dc';
const SURFACE = '#f6faf4';
const BAR = '#1f7a6f';

/** How many of the 36 dice combinations make each total. */
const WAYS = [0, 0, 1, 2, 3, 4, 5, 6, 5, 4, 3, 2, 1];

/** A clean tick step that gives about `target` ticks over `max`. */
function step(max: number, target: number): number {
  const raw = Math.max(1, max / target);
  for (const s of [1, 2, 5, 10, 20, 25, 50, 100, 200, 500]) if (s >= raw) return s;
  return 1000;
}

/** Column with a 4px rounded data end and a square baseline. */
function column(x: number, w: number, top: number, base: number): string {
  const r = Math.min(4, w / 2, base - top);
  return `M${x},${base} V${top + r} Q${x},${top} ${x + r},${top} H${x + w - r} Q${x + w},${top} ${x + w},${top + r} V${base} Z`;
}

/** Near-white player colours vanish on the panel, so their lines get a dark casing. */
function needsCasing(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  return ((n >> 16) & 255) * 0.299 + ((n >> 8) & 255) * 0.587 + (n & 255) * 0.114 > 200;
}

function PointsChart({ stats, names, target }: { stats: GameStats; names: readonly string[]; target: number }) {
  const playerColor = useSeatColor();
  const [hover, setHover] = useState<number | null>(null);
  const series = stats.points;
  const n = series.length;
  if (n < 2) return null;
  const max = Math.max(target, ...series.flat());
  const yStep = step(max, 5);
  const top = Math.ceil(max / yStep) * yStep;
  const x = (i: number) => PAD.left + (i / (n - 1)) * PLOT_W;
  const y = (v: number) => PAD.top + PLOT_H - (v / top) * PLOT_H;
  const xStep = step(n - 1, 6);
  const players = names.map((_, p) => p);
  const at = hover ?? n - 1;

  const pick = (e: PointerEvent) => {
    const svg = e.currentTarget as SVGSVGElement;
    const rect = svg.getBoundingClientRect();
    const vx = ((e.clientX - rect.left) / rect.width) * W;
    setHover(Math.max(0, Math.min(n - 1, Math.round(((vx - PAD.left) / PLOT_W) * (n - 1)))));
  };
  const key = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setHover(Math.max(0, Math.min(n - 1, at + (e.key === 'ArrowLeft' ? -1 : 1))));
    }
  };
  const final = at === n - 1;
  const leftSide = x(at) > W / 2;

  return (
    <figure class="chart">
      <figcaption>Points over the game</figcaption>
      <div class="chart-frame">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label="Points for each player over the game; the table below has the final numbers"
          tabindex={0}
          onPointerMove={pick}
          onPointerLeave={() => setHover(null)}
          onKeyDown={key}
          onBlur={() => setHover(null)}
          data-testid="points-chart"
        >
          {Array.from({ length: top / yStep + 1 }, (_, i) => i * yStep).map((v) => (
            <g key={`y${v}`}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke={GRID} stroke-width={1} />
              <text x={PAD.left - 6} y={y(v) + 3.5} text-anchor="end" class="axis">
                {v}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={W - PAD.right} y1={y(target)} y2={y(target)} stroke={MUTED} stroke-width={1} />
          <text x={W - PAD.right} y={y(target) - 4} text-anchor="end" class="axis">
            Goal {target}
          </text>
          {Array.from({ length: Math.floor((n - 1) / xStep) + 1 }, (_, i) => i * xStep).map((t) => (
            <text key={`x${t}`} x={x(t)} y={H - PAD.bottom + 14} text-anchor="middle" class="axis">
              {t}
            </text>
          ))}
          <text x={PAD.left + PLOT_W / 2} y={H - 3} text-anchor="middle" class="axis">
            Turn
          </text>
          {hover !== null && <line x1={x(at)} x2={x(at)} y1={PAD.top} y2={PAD.top + PLOT_H} stroke={MUTED} stroke-width={1} />}
          {players.map((p) => {
            const d = series.map((row, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(row[p] ?? 0).toFixed(1)}`).join(' ');
            const color = playerColor(p);
            return (
              <g key={p}>
                {needsCasing(color) && <path d={d} fill="none" stroke={INK} stroke-width={4} stroke-linejoin="round" stroke-linecap="round" />}
                <path d={d} fill="none" stroke={color} stroke-width={2} stroke-linejoin="round" stroke-linecap="round" />
              </g>
            );
          })}
          {players.map((p) => (
            <circle
              key={`dot${p}`}
              cx={x(at)}
              cy={y(series[at]![p] ?? 0)}
              r={4}
              fill={playerColor(p)}
              stroke={needsCasing(playerColor(p)) ? INK : SURFACE}
              stroke-width={needsCasing(playerColor(p)) ? 1.5 : 2}
            />
          ))}
        </svg>
        {hover !== null && (
          <div class={`chart-tip${leftSide ? ' left' : ''}`} style={{ left: `${(x(at) / W) * 100}%` }} data-testid="points-tip">
            <div class="tip-head">{final ? 'Final' : `Turn ${at}`}</div>
            {[...players]
              .sort((a, b) => (series[at]![b] ?? 0) - (series[at]![a] ?? 0))
              .map((p) => (
                <div class="tip-row" key={p}>
                  <span class="line-key" style={{ background: playerColor(p) }} />
                  <strong>{series[at]![p]}</strong>
                  <span class="muted">{names[p]}</span>
                </div>
              ))}
          </div>
        )}
      </div>
      <ul class="legend">
        {players.map((p) => (
          <li key={p}>
            <span class="line-key" style={{ background: playerColor(p) }} />
            {names[p]}
          </li>
        ))}
      </ul>
    </figure>
  );
}

function DiceChart({ stats }: { stats: GameStats }) {
  const [hover, setHover] = useState<number | null>(null);
  const totals = Array.from({ length: 11 }, (_, i) => i + 2);
  const rolls = stats.rolls.reduce((a, b) => a + b, 0);
  if (rolls === 0) return null;
  const expected = (t: number) => (rolls * WAYS[t]!) / 36;
  const max = Math.max(...totals.map((t) => Math.max(stats.rolls[t] ?? 0, expected(t))));
  const yStep = step(max, 4);
  const top = Math.ceil(max / yStep) * yStep;
  const slot = PLOT_W / totals.length;
  const barW = Math.min(24, slot - 4);
  const base = PAD.top + PLOT_H;
  const y = (v: number) => base - (v / top) * PLOT_H;
  const most = totals.reduce((a, b) => ((stats.rolls[b] ?? 0) > (stats.rolls[a] ?? 0) ? b : a));
  const shown = hover ?? null;

  return (
    <figure class="chart">
      <figcaption>
        Dice rolls <span class="muted">({rolls} rolls)</span>
      </figcaption>
      <div class="chart-frame">
        <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={`How often each total was rolled, against what fair dice would give over ${rolls} rolls`} data-testid="dice-chart">
          {Array.from({ length: top / yStep + 1 }, (_, i) => i * yStep).map((v) => (
            <g key={`y${v}`}>
              <line x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} stroke={GRID} stroke-width={1} />
              <text x={PAD.left - 6} y={y(v) + 3.5} text-anchor="end" class="axis">
                {v}
              </text>
            </g>
          ))}
          {totals.map((t, i) => {
            const cx = PAD.left + slot * i + slot / 2;
            const count = stats.rolls[t] ?? 0;
            const active = shown === t;
            return (
              <g key={t}>
                {count > 0 && <path d={column(cx - barW / 2, barW, y(count), base)} fill={BAR} opacity={shown === null || active ? 1 : 0.55} />}
                <line x1={cx - barW / 2 - 3} x2={cx + barW / 2 + 3} y1={y(expected(t))} y2={y(expected(t))} stroke={INK} stroke-width={2} stroke-linecap="round" />
                <text x={cx} y={base + 14} text-anchor="middle" class="axis">
                  {t}
                </text>
                {t === most && (
                  <text x={cx} y={y(count) - 6} text-anchor="middle" class="value">
                    {count}
                  </text>
                )}
                {/* The whole slot is the hit target, not just the painted column. */}
                <rect
                  x={cx - slot / 2}
                  y={PAD.top}
                  width={slot}
                  height={PLOT_H + 18}
                  fill="transparent"
                  tabindex={0}
                  role="button"
                  aria-label={`${t}: rolled ${count} times, about ${expected(t).toFixed(1)} expected`}
                  onPointerEnter={() => setHover(t)}
                  onPointerLeave={() => setHover(null)}
                  onFocus={() => setHover(t)}
                  onBlur={() => setHover(null)}
                />
              </g>
            );
          })}
        </svg>
        {shown !== null && (
          <div
            class={`chart-tip${shown > 7 ? ' left' : ''}`}
            style={{ left: `${((PAD.left + slot * (shown - 2) + slot / 2) / W) * 100}%` }}
            data-testid="dice-tip"
          >
            <div class="tip-head">Rolled {shown}</div>
            <div class="tip-row">
              <strong>{stats.rolls[shown] ?? 0}</strong>
              <span class="muted">times</span>
            </div>
            <div class="tip-row">
              <strong>{expected(shown).toFixed(1)}</strong>
              <span class="muted">expected</span>
            </div>
          </div>
        )}
      </div>
      <ul class="legend">
        <li>
          <span class="bar-key" style={{ background: BAR }} />
          Rolled
        </li>
        <li>
          <span class="line-key" style={{ background: INK }} />
          Expected with fair dice
        </li>
      </ul>
    </figure>
  );
}

function StatsTable({ view, names, stats }: { view: GameView; names: readonly string[]; stats: GameStats }) {
  const playerColor = useSeatColor();
  const final = stats.points[stats.points.length - 1] ?? [];
  const rows = names.map((_, p) => p).sort((a, b) => (final[b] ?? 0) - (final[a] ?? 0));
  const stats_: { label: string; value: (p: number) => number }[] = [
    { label: 'Points', value: (p) => final[p] ?? view.players[p]!.victoryPoints },
    { label: 'Cards from dice', value: (p) => stats.produced[p] ?? 0 },
    { label: 'Blocked by the robber', value: (p) => stats.blocked[p] ?? 0 },
    { label: 'Taken from others', value: (p) => stats.stolen[p] ?? 0 },
    { label: 'Lost to others', value: (p) => stats.lost[p] ?? 0 },
    { label: 'Discarded on 7s', value: (p) => stats.discarded[p] ?? 0 },
    { label: 'Bank trades', value: (p) => stats.bankTrades[p] ?? 0 },
    { label: 'Player trades', value: (p) => stats.playerTrades[p] ?? 0 },
  ];
  // Players across the top, ranked: fits a phone up to six players, and scrolls past that.
  return (
    <div class="table-scroll">
      <table class="stats-table" data-testid="stats-table">
        <thead>
          <tr>
            <th />
            {rows.map((p) => (
              <th key={p} scope="col">
                <span class="dot" style={{ background: playerColor(p) }} />
                <span class="stats-name">{names[p]}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {stats_.map((row) => (
            <tr key={row.label}>
              <th scope="row">{row.label}</th>
              {rows.map((p) => (
                <td key={p}>{row.value(p)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** End-of-game summary. Games from before stats were kept have nothing to show. */
export function GameSummary({ view, names }: { view: GameView; names: readonly string[] }) {
  const stats = view.stats;
  if (!stats) return null;
  return (
    <div class="panel summary" data-testid="summary">
      <h2>Game summary</h2>
      <StatsTable view={view} names={names} stats={stats} />
      <PointsChart stats={stats} names={names} target={view.config.victoryPointsToWin} />
      <DiceChart stats={stats} />
    </div>
  );
}
